import { replaceListPreserving } from '../common-utils.js';
import { backgroundCheckStatusSchema } from './coach-shared.js';
import { BackgroundCheckStatusLabelMap } from './coach.js';

const { options, enum: BackgroundCheckStatusEnum } = backgroundCheckStatusSchema;
export type BackgroundCheckStatusValue = (typeof options)[number];

const {
  checkr_passed,
  california_fingerprint_passed,
  ebr_fingerprint_processing,
  ebr_fingerprint_passed,
  ebr_fingerprint_pending_or_passed,
  ebr_fingerprint_quality_reject,
  uva_fingerprint_processing,
  uva_cleared,
  uva_fingerprint_pending_or_cleared,
  florida_fingerprint_processing,
  florida_fingerprint_passed,
  florida_fingerprint_pending_or_passed,
  florida_foundations_course_completed,
  louisiana_can,
} = BackgroundCheckStatusEnum;

/** Jurisdiction pairs: processing status conflicts with final clearance. */
export const BACKGROUND_CHECK_JURISDICTION_PAIRS = [
  {
    jurisdiction: 'ebr',
    processing: ebr_fingerprint_processing,
    cleared: ebr_fingerprint_passed,
    pendingOrCleared: ebr_fingerprint_pending_or_passed,
  },
  {
    jurisdiction: 'uva',
    processing: uva_fingerprint_processing,
    cleared: uva_cleared,
    pendingOrCleared: uva_fingerprint_pending_or_cleared,
  },
  {
    jurisdiction: 'florida',
    processing: florida_fingerprint_processing,
    cleared: florida_fingerprint_passed,
    pendingOrCleared: florida_fingerprint_pending_or_passed,
  },
] as const;

export type BackgroundCheckJurisdiction = (typeof BACKGROUND_CHECK_JURISDICTION_PAIRS)[number]['jurisdiction'];

/** Statuses that belong on a teacher profile / Availabilities filter (not district-only requirements). */
export const TEACHER_BACKGROUND_CHECK_STATUSES: readonly BackgroundCheckStatusValue[] = [
  checkr_passed,
  ebr_fingerprint_passed,
  ebr_fingerprint_processing,
  ebr_fingerprint_quality_reject,
  california_fingerprint_passed,
  florida_fingerprint_passed,
  florida_fingerprint_processing,
  florida_foundations_course_completed,
  uva_cleared,
  uva_fingerprint_processing,
  louisiana_can,
];

/**
 * Statuses offered when editing district requirements.
 * Legacy `ebr_fingerprint_quality_reject` remains valid on existing rows but is not offered for new edits.
 */
export const DISTRICT_BACKGROUND_CHECK_STATUSES: readonly BackgroundCheckStatusValue[] = [
  checkr_passed,
  ebr_fingerprint_pending_or_passed,
  ebr_fingerprint_passed,
  uva_fingerprint_pending_or_cleared,
  uva_cleared,
  california_fingerprint_passed,
  florida_fingerprint_pending_or_passed,
  florida_fingerprint_passed,
  florida_foundations_course_completed,
  louisiana_can,
];

const DISTRICT_ONLY_BACKGROUND_CHECK_STATUSES = new Set<BackgroundCheckStatusValue>([
  ebr_fingerprint_pending_or_passed,
  uva_fingerprint_pending_or_cleared,
  florida_fingerprint_pending_or_passed,
]);

export const V2_MANUAL_BACKGROUND_CHECK_STATUSES = new Set<BackgroundCheckStatusValue>([louisiana_can]);

export function isDistrictOnlyBackgroundCheckStatus(status: BackgroundCheckStatusValue): boolean {
  return DISTRICT_ONLY_BACKGROUND_CHECK_STATUSES.has(status);
}

interface BackgroundCheckPair {
  processing: BackgroundCheckStatusValue;
  cleared: BackgroundCheckStatusValue;
}

export function findConflictingBackgroundCheckPair(
  statuses: readonly BackgroundCheckStatusValue[],
): BackgroundCheckPair | null {
  const set = new Set(statuses);

  for (const { processing, cleared } of BACKGROUND_CHECK_JURISDICTION_PAIRS) {
    if (set.has(processing) && set.has(cleared)) {
      return { processing, cleared };
    }
  }

  return null;
}

export function assertNoConflictingBackgroundCheckStatuses(statuses: readonly BackgroundCheckStatusValue[]): void {
  const conflict = findConflictingBackgroundCheckPair(statuses);

  if (conflict) {
    throw new TypeError(
      `Cannot set both "${BackgroundCheckStatusLabelMap[conflict.processing]}" and "${BackgroundCheckStatusLabelMap[conflict.cleared]}" on the same teacher`,
    );
  }
}

/**
 * A district may select at most one option per fingerprint jurisdiction (EBR / UVA / Florida).
 */
export function findConflictingDistrictBackgroundCheckJurisdiction(
  statuses: readonly BackgroundCheckStatusValue[],
): BackgroundCheckJurisdiction | null {
  const set = new Set(statuses);

  for (const { pendingOrCleared, cleared, jurisdiction } of BACKGROUND_CHECK_JURISDICTION_PAIRS) {
    const selected = [pendingOrCleared, cleared].filter((status) => set.has(status));

    if (selected.length > 1) {
      return jurisdiction;
    }
  }

  return null;
}

export function assertDistrictBackgroundCheckStatusesValid(statuses: readonly BackgroundCheckStatusValue[]): void {
  const allowedStatuses = new Set([...DISTRICT_BACKGROUND_CHECK_STATUSES, ebr_fingerprint_quality_reject]);
  const invalidStatus = statuses.find((status) => !allowedStatuses.has(status));

  if (invalidStatus) {
    throw new TypeError(`Invalid district background check option "${BackgroundCheckStatusLabelMap[invalidStatus]}"`);
  }

  const conflict = findConflictingDistrictBackgroundCheckJurisdiction(statuses);

  if (conflict) {
    throw new TypeError(`A district can select at most one ${conflict.toUpperCase()} background check option`);
  }
}

function coachSatisfiesSingleRequirement(
  coachChecks: ReadonlySet<BackgroundCheckStatusValue>,
  requirement: BackgroundCheckStatusValue,
): boolean {
  for (const { pendingOrCleared, processing, cleared } of BACKGROUND_CHECK_JURISDICTION_PAIRS) {
    if (requirement === pendingOrCleared) {
      return coachChecks.has(processing) || coachChecks.has(cleared);
    }

    if (requirement === cleared) {
      return coachChecks.has(cleared);
    }
  }

  return coachChecks.has(requirement);
}

/**
 * Whether a coach satisfies an org's background-check requirements.
 *
 * - Exact statuses must be present on the coach (AND across requirements).
 * - "Pending or passed/cleared" accepts that jurisdiction's processing or final clearance.
 * - Legacy `ebr_fingerprint_quality_reject` on the org is stripped; if the org "requires EBR"
 *   only via quality reject (no passed / pending-or-passed), nobody is eligible.
 */
export function coachPassesBackgroundCheckRequirements(
  orgRequirements: readonly BackgroundCheckStatusValue[],
  coachStatuses: readonly BackgroundCheckStatusValue[] | null | undefined,
): boolean {
  const orgRequiresEbr =
    orgRequirements.includes(ebr_fingerprint_passed) ||
    orgRequirements.includes(ebr_fingerprint_pending_or_passed) ||
    orgRequirements.includes(ebr_fingerprint_quality_reject);

  const effectiveChecks = orgRequirements.filter((status) => status !== ebr_fingerprint_quality_reject);

  if (
    orgRequiresEbr &&
    !effectiveChecks.includes(ebr_fingerprint_passed) &&
    !effectiveChecks.includes(ebr_fingerprint_pending_or_passed)
  ) {
    return false;
  }

  const coachChecks = new Set(coachStatuses ?? []);
  return effectiveChecks.every((requirement) => coachSatisfiesSingleRequirement(coachChecks, requirement));
}

/**
 * Lower number = higher rank. Cleared teachers (0) rank ahead of teachers who only meet a
 * pending-or-passed requirement via processing (1).
 */
export function fingerprintClearanceRank(
  orgRequirements: readonly BackgroundCheckStatusValue[],
  coachStatuses: readonly BackgroundCheckStatusValue[] | null | undefined,
): number {
  const coachChecks = new Set(coachStatuses ?? []);

  for (const { pendingOrCleared, cleared, processing } of BACKGROUND_CHECK_JURISDICTION_PAIRS) {
    if (!orgRequirements.includes(pendingOrCleared)) {
      continue;
    }

    if (coachChecks.has(cleared)) {
      continue;
    }

    if (coachChecks.has(processing)) {
      return 1;
    }
  }

  return 0;
}

/**
 * Merge incoming background-check statuses (CSV / v1) with existing teacher statuses.
 *
 * - Rejects payloads that contain both processing and final clearance for the same check.
 * - Preserves manual-only statuses (e.g. louisiana_can) via {@link replaceListPreserving}.
 * - If incoming supplies final clearance, removes the matching processing status.
 * - If incoming supplies no result for a check, retains an existing processing status.
 */
export function mergeBackgroundCheckStatusesFromExternalSource(
  existing: readonly BackgroundCheckStatusValue[],
  incoming: readonly BackgroundCheckStatusValue[],
  keepManual: ReadonlySet<BackgroundCheckStatusValue> = V2_MANUAL_BACKGROUND_CHECK_STATUSES,
): BackgroundCheckStatusValue[] {
  assertNoConflictingBackgroundCheckStatuses(incoming);

  const result = replaceListPreserving([...existing], [...incoming], keepManual);

  for (const { cleared, processing } of BACKGROUND_CHECK_JURISDICTION_PAIRS) {
    const incomingHasCleared = incoming.includes(cleared);
    const incomingHasProcessing = incoming.includes(processing);
    const incomingMentionsJurisdiction = incomingHasCleared || incomingHasProcessing;

    if (incomingHasCleared) {
      const withoutProcessing = result.filter((status) => status !== processing);
      result.length = 0;
      result.push(...withoutProcessing);
    } else if (!incomingMentionsJurisdiction && existing.includes(processing) && !result.includes(processing)) {
      result.push(processing);
    }
  }

  assertNoConflictingBackgroundCheckStatuses(result);
  return result;
}

/**
 * Exact v1 / Airtable "Background check status" labels → v2 enum values.
 * Shared by sync (`backgroundCheckStatusesFrom`) and coach CSV parsing.
 */
export const V1_BACKGROUND_CHECK_STATUS_LABELS: Readonly<Record<string, BackgroundCheckStatusValue>> = {
  'Checkr Passed': checkr_passed,
  'EBR Background Check - Cleared Spring/Summer 2025': ebr_fingerprint_passed,
  'EBR Fingerprint Processing': ebr_fingerprint_processing,
  'Louisiana Fingerprint quality rejection': ebr_fingerprint_quality_reject,
  'California Fingerprint Passed': california_fingerprint_passed,
  'Florida Fingerprint Passed': florida_fingerprint_passed,
  'Florida Fingerprint Processing': florida_fingerprint_processing,
  'Florida Foundations Course Completed': florida_foundations_course_completed,
  'UVA Cleared': uva_cleared,
  'UVA Fingerprint Processing': uva_fingerprint_processing,
};

/**
 * Maps v1 / Airtable "Background check status" labels to v2 enum values.
 * Unknown labels are dropped.
 */
export function backgroundCheckStatusesFrom(list: string[]): BackgroundCheckStatusValue[] {
  return list.flatMap((value) => {
    const mapped = V1_BACKGROUND_CHECK_STATUS_LABELS[value];
    return mapped ? [mapped] : [];
  });
}
